import type { IconGlyph } from '@ipfabric/ui-components/Icon/IconGlyph';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import {
  type ApiScopeRequirement,
  type GraphLocationDescriptor,
  IPF_NETWORK_VIEWER,
  type OasDocument3XExtended,
  createPathlookupGraphLink,
  createTopologyGraphLink,
} from '@ipfabric/utils';
import type { LicenseAdditionalFeatureName } from '@ipfabric/utils/license/licenseSchema';
import type { AppTranslations } from 'translations/translations';
import type { PermissionScope } from 'utils/hooks/usePermissionScope';
import { createMenuFromOas } from './createMenuFromOas';
import { mergeMenuTree } from './mergeMenuTree';

export type PageTitleTranslations = {
  boxTitle?: string;
  pageTitle?: string;

  // type no-op that avoids type error "no common properties"
  // when page translations do not contain any of the attributes above
  [key: string]: unknown;
};

export type PageItem = {
  boxTitle?: string;
  url: string;
  msg: PageTitleTranslations;
  apiEndpoint?: string;
  path?: string[];
};

/**
 * Use PageItem to add page to search results and DeviceView setup tables window. Both need 'msg' to get page text.
 * Otherwiser, use string.
 */
export type UrlListItem =
  | PageItem
  | string
  | ((path: string) => boolean)
  | GraphLocationDescriptor;

export const isPageItem = (item?: UrlListItem): item is PageItem =>
  item !== undefined &&
  typeof item !== 'string' &&
  typeof item !== 'function' &&
  Object.hasOwnProperty.call(item, 'msg');

export const isGraphLocationDescriptor = (
  x: UrlListItem | undefined,
): x is GraphLocationDescriptor => Object.hasOwnProperty.call(x, 'state');

export type MenuTreeNode = {
  boxTitle?: string;
  label: string;
  children?: MenuTreeNode[];
  msg?: PageTitleTranslations;
  iconClassName?: IconGlyph;
  permissions?: PermissionScope[];
  /** Unique identifier of the menu item */
  _key?: string;
  urlList?: UrlListItem[];
  apiScopeRequirement?: ApiScopeRequirement;
  /**
   * Hide menu item when user has no access to given route.
   *
   * Route path key is parsed in selectAllRoutesByPath simply constructed from parent paths and route path,
   * so e.g. route defined as '/foo' (parent) -> 'bar/*' will be accessible under '/foo/bar/*'.
   */
  restrictByRoute?: string;
  /** Shows "beta" badge in menu item */
  preview?: boolean;
  /**
   * Hide menu item at runtime unless the given feature flag is enabled.
   *
   * Carried over from the OAS `x-webPages` tree so feature-flag gating happens in the
   * running app (the OAS is generated once, without dev feature flags) rather than being
   * trimmed out at OAS build time.
   */
  'x-required-feature-flag'?: string;
  /**
   * Hide menu item at runtime unless the given license feature is valid.
   *
   * Carried over from the OAS `x-webPages` tree; evaluated at runtime against the
   * current verified license.
   */
  'x-require-license-feature'?: LicenseAdditionalFeatureName;
};

const pathWithSubpaths = (path: string): UrlListItem[] => [
  path,
  (pathname) => pathname.startsWith(`${path}/`),
];

export const createMenuTree = (
  msg: AppTranslations,
  oas: OasDocument3XExtended | null,
  selectedSnapshot: string | null,
): MenuTreeNode[] => [
  {
    _key: 'snapshotMgmt',
    iconClassName: 'fa-rocket-launch',
    label: msg.app.sidebar.snapshotManagement,
    restrictByRoute: '/snapshot-management',
    msg: msg.snapshotManagement,
    urlList: [
      selectedSnapshot
        ? `/snapshot-management/${selectedSnapshot}`
        : '/snapshot-management/new',
      (pathname: string) => pathname.startsWith('/snapshot-management'),
    ],
  },
  {
    _key: 'dashboard',
    label: msg.app.sidebar.dashboard,
    msg: msg.dashboardOverview,
    restrictByRoute: '/dashboard',
    iconClassName: 'fa-gauge-circle-bolt',
    urlList: ['/dashboard/overview'],
  },
  {
    _key: 'reports',
    label: msg.app.sidebar.reports,
    iconClassName: 'fa-file-lines',
    children: [
      {
        _key: 'reportsHistoryNetwork',
        label: msg.app.sidebar.reportsHistoryNetwork,
        urlList: [
          {
            msg: msg.networkAnalysisReport,
            url: '/reports/network-analysis-report',
          },
        ],
      },
      {
        _key: 'reportsHistorySites',
        label: msg.app.sidebar.reportsHistorySites,
        msg: msg.siteLowLevelDesign,
        urlList: [
          {
            msg: msg.siteLowLevelDesign,
            url: '/reports/site-low-level-design',
          },
        ],
      },
    ],
  },
  {
    _key: 'inventory',
    label: msg.app.sidebar.inventory,
    iconClassName: 'fa-box-archive',
    children: oas ? createMenuFromOas(oas, 'inventory') : [],
  },
  {
    _key: 'diagrams',
    label: msg.app.sidebar.diagrams,
    iconClassName: 'fa-chart-network',
    children: [
      {
        _key: 'diagramsNetwork',
        label: msg.app.sidebar.diagramsNetwork,
        msg: msg.canvasGraph,
        restrictByRoute: '/diagrams/topology-tree',
        urlList: [
          createTopologyGraphLink({
            parameters: {
              groupBy: 'siteName',
              paths: [IPF_NETWORK_VIEWER],
            },
            sidePanelContentId: 'networkViewer',
          }),
        ],
      },
      {
        _key: 'diagramsSites',
        label: msg.app.sidebar.diagramsSites,
        restrictByRoute: '/diagrams/topology-tree',
        urlList: [
          createTopologyGraphLink({
            parameters: {
              groupBy: 'siteName',
              paths: [],
            },
            sidePanelContentId: 'networkViewer',
          }),
        ],
      },
      {
        _key: 'diagramsRouting',
        label: msg.app.sidebar.diagramsRouting,
        msg: msg.canvasGraph,
        restrictByRoute: '/diagrams/topology-tree',
        urlList: [
          createTopologyGraphLink({
            parameters: {
              groupBy: 'routingDomain',
              paths: [],
            },
            sidePanelContentId: 'networkViewer',
          }),
        ],
      },
      {
        _key: 'diagramsSwitching',
        label: msg.app.sidebar.diagramsSwitching,
        msg: msg.canvasGraph,
        restrictByRoute: '/diagrams/topology-tree',
        urlList: [
          createTopologyGraphLink({
            parameters: {
              groupBy: 'stpDomain',
              paths: [],
            },
            sidePanelContentId: 'networkViewer',
          }),
        ],
      },
      {
        _key: 'diagramsPathLookup',
        label: msg.app.sidebar.diagramsPathLookup,
        restrictByRoute: '/diagrams/pathlookup',
        urlList: [
          createPathlookupGraphLink({
            sidePanelContentId: 'pathlookup',
            pathLookupDataInputTabId: 'unicast',
          }),
        ],
      },
      {
        _key: 'diagramsHostLookup',
        label: msg.app.sidebar.diagramsHostLookup,
        msg: msg.canvasGraph,
        restrictByRoute: '/diagrams/pathlookup',
        urlList: [
          createPathlookupGraphLink({
            sidePanelContentId: 'pathlookup',
            pathLookupDataInputTabId: 'hostToDefaultGW',
          }),
        ],
      },
    ],
  },
  {
    _key: 'management',
    label: msg.app.sidebar.management,
    iconClassName: 'fa-display-code',
    children: [
      {
        _key: 'managementConfiguration',
        label: msg.app.sidebar.managementConfiguration,
        urlList: [
          {
            msg: { title: 'Configuration' },
            url: '/management/configuration',
          },
          {
            msg: { title: 'Configuration Connectivity Errors' },
            url: '/management/configuration/connectivity-errors',
          },
        ],
      },
      {
        _key: 'managementChanges',
        label: msg.app.sidebar.managementChanges,
        urlList: [
          {
            msg: { title: 'Snapshot Changes' },
            url: '/management/changes',
          },
          (pathname: string) => pathname.startsWith('/management/changes/'),
        ],
      },
      {
        _key: 'managementDiscoveryHistory',
        label: msg.app.sidebar.managementDiscoveryHistory,
        urlList: ['/management/discovery-history'],
      },
      {
        _key: 'managementSavedConfigConsistency',
        label: msg.app.sidebar.managementSavedConfigConsistency,
        msg: msg.managementSavedConfigConsistency,
        urlList: [
          {
            msg: msg.managementSavedConfigConsistency,
            url: '/management/saved-config-consistency',
          },
        ],
      },
    ],
  },
  {
    _key: 'technology',
    label: msg.app.sidebar.technology,
    iconClassName: 'fa-router',
    children: oas ? createMenuFromOas(oas, 'technology') : [],
  },
  ...(isFeatureFlagEnabled('ENABLE_EXTENSIONS')
    ? [
        {
          _key: 'extensions',
          label: msg.app.sidebar.extensions,
          restrictByRoute: '/extensions',
          iconClassName: 'fa-puzzle-piece',
          urlList: ['/extensions/overview'],
          preview: true,
        } satisfies MenuTreeNode,
      ]
    : []),
  {
    _key: 'settings',
    label: msg.app.sidebar.settings,
    iconClassName: 'fa-gear',
    children: mergeMenuTree([
      [
        {
          _key: 'settingsDiscoverySnapshots',
          label: msg.app.sidebar.settingsDiscoverySnapshots,
          restrictByRoute: '/settings/discovery-snapshots',
          children: [
            {
              _key: 'settingsGlobalDiscoveryConfiguration',
              label: msg.app.sidebar.settingsGlobalDiscoveryConfiguration,
              urlList: [
                '/settings/discovery-snapshots/global-discovery-configuration/device-attributes',
                '/settings/discovery-snapshots/global-discovery-configuration/jumphost',
                '/settings/discovery-snapshots/global-discovery-configuration/oui',
              ],
            },
            {
              _key: 'settingsDiscoverySettings',
              label: msg.app.sidebar.settingsGlobalDiscoverySettings,
              urlList: [
                '/settings/discovery-snapshots/global-discovery-settings',
              ],
            },
            ...(isFeatureFlagEnabled('ENABLE_LABELS')
              ? [
                  {
                    _key: 'settingsDiscoveryDataEnrichment',
                    label: msg.app.sidebar.settingsDiscoveryDataEnrichment,
                    urlList: [
                      '/settings/discovery-snapshots/discovery-data-enrichment',
                    ],
                  },
                ]
              : []),
            {
              _key: 'settingsSnapshotCollection',
              label: msg.app.sidebar.settingsSnapshotCollection,
              urlList: ['/settings/discovery-snapshots/snapshot-collection'],
            },
          ],
        },
        {
          _key: 'settingsConfigurationManagement',
          label: msg.app.sidebar.settingsConfigurationManagement,
          urlList: ['/settings/configuration-management'],
        },
        {
          _key: 'settingsIntegration',
          label: msg.app.sidebar.settingsIntegration,
          children: [
            {
              _key: 'settingsApiTokens',
              label: msg.app.sidebar.settingsApiTokens,
              urlList: ['/settings/integration/api-tokens'],
            },
            {
              _key: 'settingsWebHooks',
              label: msg.app.sidebar.settingsWebHooks,
              restrictByRoute: '/settings/integration/webhooks/*',
              urlList: [
                '/settings/integration/webhooks',
                (pathname: string) =>
                  pathname.startsWith('/settings/integration/webhooks/'),
              ],
            },
            {
              _key: 'settingsMcpServer',
              label: msg.app.sidebar.settingsMcpServer,
              urlList: ['/settings/integration/mcp-server'],
            },
          ],
        },
        {
          _key: 'settingsSystem',
          label: msg.app.sidebar.settingsSystem,
          restrictByRoute: '/settings/system',
          children: [
            {
              _key: 'settingsSnmp',
              label: msg.app.sidebar.settingsSnmp,
              restrictByRoute: '/settings/system/snmp',
              urlList: pathWithSubpaths('/settings/system/snmp'),
            },
            {
              _key: 'settingsIpfCertificates',
              label: msg.app.sidebar.settingsIpfCertificates,
              urlList: pathWithSubpaths(
                '/settings/system/ipfabric-certificates',
              ),
            },
            {
              _key: 'settingsCertificationAuthorities',
              label: msg.app.sidebar.settingsCertificationAuthorities,
              restrictByRoute: '/settings/system/certificate-authorities',
              urlList: pathWithSubpaths(
                '/settings/system/certificate-authorities',
              ),
            },
            {
              _key: 'settingsBackupMaintenance',
              label: msg.app.sidebar.settingsBackupMaintenance,
              restrictByRoute: '/settings/system/backup-maintenance',
              urlList: pathWithSubpaths('/settings/system/backup-maintenance'),
            },
          ],
        },
        {
          _key: 'settingsAdministration',
          label: msg.app.sidebar.settingsAdministration,
          restrictByRoute: '/settings/administration',
          children: [
            {
              _key: 'settingsUserManagementLocalUsers',
              label: msg.app.sidebar.settingsUserManagementLocalUsers,
              restrictByRoute: '/settings/administration/local-users',
              urlList: pathWithSubpaths('/settings/administration/local-users'),
            },
            {
              _key: 'settingsUserManagementLdap',
              label: msg.app.sidebar.settingsUserManagementLdap,
              restrictByRoute: '/settings/administration/ldap',
              urlList: pathWithSubpaths('/settings/administration/ldap'),
            },
            {
              _key: 'settingsUserManagementRoles',
              label: msg.app.sidebar.settingsUserManagementRoles,
              restrictByRoute: '/settings/administration/roles',
              urlList: pathWithSubpaths('/settings/administration/roles'),
            },
            ...(isFeatureFlagEnabled('ENABLE_LABELS')
              ? [
                  {
                    _key: 'settingsUserManagementEndpointPolicies',
                    label: 'Endpoint policies',
                    restrictByRoute:
                      '/settings/administration/endpoint-policies',
                    urlList: pathWithSubpaths(
                      '/settings/administration/endpoint-policies',
                    ),
                  },
                  {
                    _key: 'settingsUserManagementDataPolicies',
                    label: 'Data policies',
                    restrictByRoute: '/settings/administration/data-policies',
                    urlList: pathWithSubpaths(
                      '/settings/administration/data-policies',
                    ),
                  },
                ]
              : []),
            // Legacy policies still enforce access, so they stay reachable
            // regardless of the labels flag.
            {
              _key: 'settingsUserManagementPolicies',
              label: msg.app.sidebar.settingsUserManagementPolicies,
              restrictByRoute: '/settings/administration/policies',
              urlList: pathWithSubpaths('/settings/administration/policies'),
            },
          ],
        },
        {
          _key: 'settingsConfigurationWizard',
          label: msg.app.sidebar.settingsConfigurationWizard,
          urlList: [
            '/wizard/1',
            (pathname: string) => pathname.startsWith('/wizard'),
          ],
          restrictByRoute: '/wizard/:step',
        },
      ],
      oas ? createMenuFromOas(oas, 'settings') : [],
    ]),
  },
];
